export type SupplierInvoiceDuplicateSource = {
  invoice: Record<string, unknown>;
  lines: Array<Record<string, unknown>>;
  allocations: Array<Record<string, unknown>>;
  documents?: unknown[];
  payments?: unknown[];
  audit?: unknown[];
};

type MoneyLike = string | number;
type DuplicateInvoiceValues = Record<string, unknown> & {
  taxableBase: MoneyLike; vatAmount: MoneyLike; totalAmount: MoneyLike; financialCategoryId?: number | null;
};
type DuplicateLineValues = Record<string, unknown> & {
  productId?: number | null; quantity: MoneyLike; unit: string; unitPrice: MoneyLike; taxableBase: MoneyLike;
  taxRate: MoneyLike; vatAmount: MoneyLike; totalAmount: MoneyLike; position: number; financialCategoryId?: number | null;
};
type DuplicateAllocationValues = Record<string, unknown> & {
  targetType: "installation" | "warehouse"; targetId?: number | string | null; allocatedAmount: MoneyLike;
  allocatedPercentage: MoneyLike; notes?: string | null;
};

export type SupplierInvoiceDuplicateSeed = {
  sourceInvoiceId: number;
  sourceInvoiceNumber: string;
  supplierId: number;
  currency: string;
  invoice: DuplicateInvoiceValues;
  line: DuplicateLineValues;
  allocation: DuplicateAllocationValues;
  categoryCode: string | null;
  form: {
    concept: string;
    allocationTarget: "installation" | "warehouse";
    installationId: string;
    taxableBase: string;
    vatRate: string;
    categoryCode: string;
    notes: string;
  };
};

export type SupplierInvoiceDuplicateResult =
  | { ok: true; seed: SupplierInvoiceDuplicateSeed }
  | { ok: false; reason: string };

const SUPPORTED_VAT_RATES = new Set(["0", "4", "10", "21"]);
const NO_CATEGORY_VALUE = "__automatic__";

function moneyCents(value: unknown): number | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const raw = String(value).trim();
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(raw)) return null;
  const negative = raw.startsWith("-");
  const unsigned = negative ? raw.slice(1) : raw;
  const [whole, fraction = ""] = unsigned.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? (negative ? -cents : cents) : null;
}

function decimalValue(value: unknown): number | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : null;
}

function supportedVat(value: unknown): string | null {
  const numeric = decimalValue(value);
  if (numeric === null || !Number.isInteger(numeric)) return null;
  const normalized = String(numeric);
  return SUPPORTED_VAT_RATES.has(normalized) ? normalized : null;
}

function asPositiveId(value: unknown): number | null {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

export function prepareSupplierInvoiceDuplicate(
  source: SupplierInvoiceDuplicateSource,
): SupplierInvoiceDuplicateResult {
  const invoice = source?.invoice;
  const sourceInvoiceId = asPositiveId(invoice?.id);
  const supplierId = asPositiveId(invoice?.supplierId);
  const sourceInvoiceNumber = typeof invoice?.invoiceNumber === "string" ? invoice.invoiceNumber.trim() : "";
  if (!sourceInvoiceId || !supplierId || !sourceInvoiceNumber) {
    return { ok: false, reason: "La factura de origen no tiene una identidad completa y no se puede duplicar." };
  }
  if (!Array.isArray(source.lines) || source.lines.length !== 1 || !Array.isArray(source.allocations) || source.allocations.length !== 1) {
    return { ok: false, reason: "La factura tiene varias o ninguna línea/imputación; este formulario no puede copiarlas sin pérdida." };
  }

  const line = source.lines[0];
  const allocation = source.allocations[0];
  const currency = typeof invoice.currency === "string" ? invoice.currency : "";
  if (!/^[A-Z]{3}$/.test(currency)) {
    return { ok: false, reason: "La moneda de origen no se puede conservar con seguridad en este formulario." };
  }

  const invoiceBase = moneyCents(invoice.taxableBase);
  const invoiceVat = moneyCents(invoice.vatAmount);
  const invoiceTotal = moneyCents(invoice.totalAmount);
  const lineBase = moneyCents(line.taxableBase);
  const linePrice = moneyCents(line.unitPrice);
  const lineVat = moneyCents(line.vatAmount);
  const lineTotal = moneyCents(line.totalAmount);
  const vatRate = supportedVat(line.taxRate);
  if ([invoiceBase, invoiceVat, invoiceTotal, lineBase, linePrice, lineVat, lineTotal].some((value) => value === null) || !vatRate) {
    return { ok: false, reason: "La base, el IVA o el tipo fiscal de origen no son representables; no se aplicará una conversión implícita." };
  }
  if (invoiceBase! < 0 || invoiceVat! < 0 || invoiceTotal! < 0 || lineBase !== invoiceBase || linePrice !== invoiceBase || lineVat !== invoiceVat || lineTotal !== invoiceTotal) {
    return { ok: false, reason: "Los importes de línea y factura no coinciden exactamente; no se recalculará ni aplanará el agregado." };
  }
  const expectedVatCents = Math.round((invoiceBase! * Number(vatRate)) / 100);
  if (expectedVatCents !== invoiceVat || invoiceBase! + invoiceVat! !== invoiceTotal) {
    return { ok: false, reason: "Los importes de origen no cuadran con el tipo IVA y el total mostrado; no se corregirán automáticamente." };
  }
  if (line.productId != null || decimalValue(line.quantity) !== 1 || line.unit !== "ud" || Number(line.position) !== 1) {
    return { ok: false, reason: "La línea tiene producto, cantidad, unidad o posición que este formulario no puede conservar." };
  }

  const invoiceCategoryId = asPositiveId(invoice.financialCategoryId);
  const lineCategoryId = asPositiveId(line.financialCategoryId);
  if (invoiceCategoryId !== lineCategoryId) {
    return { ok: false, reason: "La categoría de cabecera y línea no coincide; no se puede representar sin cambiar la clasificación." };
  }
  const categoryCode = typeof invoice.categoryCode === "string" && invoice.categoryCode.trim()
    ? invoice.categoryCode.trim()
    : null;
  if (invoiceCategoryId && !categoryCode) {
    return { ok: false, reason: "La categoría existente no tiene un código editable disponible para conservarla." };
  }

  if (allocation.targetType !== "installation" && allocation.targetType !== "warehouse") {
    return { ok: false, reason: "El destino de imputación no está soportado por este formulario." };
  }
  const targetId = allocation.targetType === "installation" ? asPositiveId(allocation.targetId) : null;
  if ((allocation.targetType === "installation" && !targetId) || (allocation.targetType === "warehouse" && allocation.targetId != null)) {
    return { ok: false, reason: "La identidad de destino de la imputación no es válida." };
  }
  const allocatedAmount = moneyCents(allocation.allocatedAmount);
  const allocatedPercentage = decimalValue(allocation.allocatedPercentage);
  if (allocatedAmount !== invoiceTotal || allocatedPercentage !== 100 || (typeof allocation.notes === "string" && allocation.notes.trim())) {
    return { ok: false, reason: "La imputación no es una asignación única del 100% que este formulario pueda preservar fielmente." };
  }

  if (typeof line.quantity !== "string" && typeof line.quantity !== "number") return { ok: false, reason: "La cantidad de origen no se puede conservar." };
  if (typeof line.unitPrice !== "string" && typeof line.unitPrice !== "number") return { ok: false, reason: "El precio de origen no se puede conservar." };
  if (typeof line.taxRate !== "string" && typeof line.taxRate !== "number") return { ok: false, reason: "El tipo IVA de origen no se puede conservar." };
  if (typeof allocation.allocatedPercentage !== "string" && typeof allocation.allocatedPercentage !== "number") return { ok: false, reason: "El porcentaje de imputación no se puede conservar." };
  const concept = typeof line.description === "string" ? line.description : "";
  if (!concept.trim()) return { ok: false, reason: "La factura de origen no tiene un concepto que pueda copiarse." };
  const originalNotes = typeof invoice.notes === "string" ? invoice.notes.trim() : "";
  const sourceMarker = `Duplicado editable de factura ${sourceInvoiceNumber} (ID ${sourceInvoiceId})`;
  const notes = [originalNotes, sourceMarker].filter(Boolean).join("\n");
  if (notes.length > 2000) return { ok: false, reason: "Las observaciones y la referencia de origen superan el límite del formulario." };

  return {
    ok: true,
    seed: {
      sourceInvoiceId,
      sourceInvoiceNumber,
      supplierId,
      currency,
      invoice: invoice as DuplicateInvoiceValues,
      line: line as DuplicateLineValues,
      allocation: allocation as DuplicateAllocationValues,
      categoryCode,
      form: {
        concept,
        allocationTarget: allocation.targetType,
        installationId: targetId ? String(targetId) : "",
        taxableBase: String(invoice.taxableBase),
        vatRate,
        categoryCode: categoryCode ?? NO_CATEGORY_VALUE,
        notes,
      },
    },
  };
}
